/**
 * Générateur — Automatismes : calculer une moyenne (1246) ; donner une
 * médiane pour une série comportant un petit nombre de valeurs (1247) ;
 * calculer l'étendue d'une série (1248).
 * Attendu ID : 1246 | Niveau : 3ème | Domaine : Statistiques
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 17, lignes 1252-1254).
 *
 * ── UN FICHIER, TROIS JEUX DE CLÉS (modèle 1101/1105) ─────────────────────
 * 1246 (clés moy_*), 1247 (clés med_*, gen_1247), 1248 (clés etd_*,
 * gen_1248) : les trois indicateurs se calculent sur LA MÊME série brute
 * (entiers 1..30, EN DÉSORDRE), et se confondent chez l'élève (la médiane
 * prise pour la moyenne, le milieu de la liste non rangée pour la médiane,
 * les extrêmes lus sans ranger). Cadrage programme-maths 2026-09-12 (mode
 * auto) : 1246 n = 3..5, moyenne entière (trois fois sur quatre) ou en ,5
 * (n = 4 seulement) ; 1247 n = 3, 5, 7 (deux fois sur trois) ou 4, 6 (la
 * demi-somme des deux centrales, À ARBITRER), valeurs distinctes, le milieu
 * de la liste écrite ≠ la médiane deux fois sur trois ; 1248 n = 3..6, le
 * premier et le dernier écrits ≠ les extrêmes. Hors : moyenne pondérée,
 * séries en tableau ou diagramme, interprétation (objectifs 4e l. 1223-1233,
 * gens 165, 214). Banque MathsMentales (S. Cogez, Apache 2.0, relevé
 * 2026-09-12) : 5DA2 options 3-4 (3e) : 2 à 5 valeurs 1..30 ; retenu n ≥ 3.
 *
 * ── Plan de cartes (par indicateur : flash, QCM, Vrai/Faux) ──────────────
 *   « Série flash » : trois lignes « Moyenne de 4 ; 9 ; 11 : ⬚ ».
 *   « Quelle moyenne / médiane / étendue ? » : QCM à quatre nombres.
 *   « Vrai ou faux » : quatre lignes « La médiane de 7 ; 3 ; 9 est 7. ».
 *
 * INVARIANTS : chaque indicateur re-calculé par un second chemin (throw) ;
 * les distracteurs distincts de la clé et entre eux ; jamais un distracteur
 * de même valeur que la clé (moyenne = médiane arrive : la famille est alors
 * retirée).
 * SACRIFIÉ (fiche « un cas par page », la série rangée) : le multiset
 * {3, 5, 8, 12, 17} (moyenne 9, médiane 8, étendue 14), dans tous les ordres
 * ; auto-testé par gen1246-1248.self.test.js.
 *
 * ── Erreurs (familles nommées ; aucun item DEPP) ─────────────────────────
 *   mauvais_effectif      : la somme divisée par n − 1 ou 2 (1246, PRINCIPALE) ;
 *   somme_seule           : la somme sans diviser (1246) ;
 *   milieu_sans_ranger    : la valeur au milieu de la liste NON rangée (1247, PRINCIPALE) ;
 *   milieu_des_extremes   : (min + max) ÷ 2 (1247, 1246) ;
 *   mediane_pour_moyenne / moyenne_pour_mediane : l'autre indicateur ;
 *   extremes_sans_ranger  : dernier − premier écrits (1248, PRINCIPALE) ;
 *   max_seul              : le maximum pris pour l'étendue (1248) ;
 *   somme_extremes        : max + min (1248) ;
 *   nombre_de_valeurs     : n pris pour l'indicateur.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou,
} from '../../../utils';

const ATTENDU_ID = 1246;
const opts = (attendu, label) => ({ niveau: '3eme', matiere: 'maths', attendu, label });

const L_FLASH = 'Série flash';
const L_MOY = 'Quelle moyenne ?';
const L_MED = 'Quelle médiane ?';
const L_ETD = 'Quelle étendue ?';
const L_VF = 'Vrai ou faux';

// ─── Le domaine ───────────────────────────────────────────────────────────

/** Les séries de la fiche, sacrifiées dans tous les ordres : l'impaire, et la paire du cas
 *  « deux valeurs au milieu » (ajouté sur demande prof 2026-09-19). */
export const SERIE_SACRIFIEE = [3, 5, 8, 12, 17];
export const SERIE_SACRIFIEE_PAIRE = [3, 5, 8, 12];
const cle = (serie) => [...serie].sort((u, v) => u - v).join(',');
const estSacrifiee = (serie) => cle(serie) === cle(SERIE_SACRIFIEE) || cle(serie) === cle(SERIE_SACRIFIEE_PAIRE);

const somme = (s) => s.reduce((a, b) => a + b, 0);
const rangee = (s) => [...s].sort((u, v) => u - v);
/** La moyenne en demi-unités entières (assert : somme × 2 divisible par n). */
const moyenneDemis = (s) => { const d = (somme(s) * 2) / s.length; if (!Number.isInteger(d) || d !== Math.round((somme(s) / s.length) * 2)) throw new Error('gen_1246 : moyenne'); return d; };
const mediane = (s) => { const r = rangee(s); const n = r.length; const m = n % 2 ? r[(n - 1) / 2] : (r[n / 2 - 1] + r[n / 2]) / 2; if (r.filter((v) => v <= m).length < n / 2 || r.filter((v) => v >= m).length < n / 2) throw new Error('gen_1246 : médiane'); return m; };
const etendue = (s) => { const e = Math.max(...s) - Math.min(...s); if (e !== rangee(s)[s.length - 1] - rangee(s)[0]) throw new Error('gen_1246 : étendue'); return e; };
/** « 8,5 » ou « 8 ». */
const fr = (x) => (Number.isInteger(x) ? String(x) : String(x).replace('.', ','));
const texteSerie = (s) => s.join(' ; ');

/**
 * Une série brute : n valeurs 1..30, en désordre (jamais rangée), hors
 * sacrifiée ; `pour` = 'moyenne' (n 3..5, moyenne entière ou ,5),
 * 'mediane' (n 3, 5, 7 ou 4, 6, valeurs distinctes, le milieu écrit ≠ la
 * médiane deux fois sur trois), 'etendue' (n 3..6, premier et dernier ≠ extrêmes).
 */
const tirer = (pour, exclure = []) => {
    for (let essai = 0; essai < 500; essai++) {
        const n = pour === 'moyenne' ? randInt(3, 5) : pour === 'mediane' ? randomChoice([3, 5, 7, 3, 5, 7, 4, 6]) : randInt(3, 6);
        const serie = [];
        while (serie.length < n) { const v = randInt(1, 30); if (pour === 'mediane' && serie.includes(v)) continue; serie.push(v); }
        const r = rangee(serie);
        if (serie.every((v, i) => v === r[i]) || estSacrifiee(serie) || exclure.includes(cle(serie))) continue;
        if (pour === 'moyenne') {
            const d = (somme(serie) * 2) / n;
            if (!Number.isInteger(d)) continue;
            if (d % 2 !== 0 && (n !== 4 || randInt(1, 4) !== 1)) continue; // ,5 rare, avec n = 4 seulement
        }
        if (pour === 'mediane' && n % 2 === 1 && serie[(n - 1) / 2] === mediane(serie) && randInt(1, 3) !== 1) continue;
        if (pour === 'etendue' && (serie[0] === r[0] || serie[n - 1] === r[n - 1]) && randInt(1, 3) !== 1) continue;
        return serie;
    }
    return pour === 'moyenne' ? [4, 11, 9] : pour === 'mediane' ? [7, 3, 9] : [9, 2, 14, 6];
};

const EXPLICATIONS = {
    mauvais_effectif: 'La moyenne, c\'est la somme divisée par le NOMBRE de valeurs : 4 + 9 + 11 = 24, et 24 ÷ 3 = 8.',
    somme_seule: 'La somme n\'est pas la moyenne : il reste à diviser par le nombre de valeurs.',
    milieu_sans_ranger: 'La médiane se lit sur la série RANGÉE : 3 ; 7 ; 9, la valeur du milieu est 7.',
    milieu_des_extremes: 'Le milieu entre le plus petit et le plus grand n\'est ni la moyenne ni la médiane.',
    mediane_pour_moyenne: 'La médiane partage la série en deux moitiés ; la moyenne, c\'est la somme divisée par le nombre de valeurs.',
    moyenne_pour_mediane: 'La moyenne se calcule ; la médiane se lit au milieu de la série rangée.',
    extremes_sans_ranger: 'L\'étendue, c\'est le plus grand MOINS le plus petit, où qu\'ils soient dans la liste.',
    max_seul: 'Le plus grand n\'est pas l\'étendue : on retire le plus petit.',
    somme_extremes: 'L\'étendue est une différence : le plus grand moins le plus petit, pas la somme.',
    nombre_de_valeurs: 'Le nombre de valeurs sert à diviser pour la moyenne, il n\'est pas la réponse.',
    regle: 'Moyenne : somme ÷ nombre de valeurs. Médiane : je range, je prends le milieu (la demi-somme des deux du milieu si c\'est pair). Étendue : plus grand − plus petit.',
};

// ─── Distracteurs (par indicateur, en demi-unités pour la moyenne) ────────

/** Les candidats pour un indicateur, chacun avec sa valeur (nombre), jamais la clé, jamais deux fois la même valeur. */
const candidats = (pour, serie) => {
    const n = serie.length; const r = rangee(serie); const S = somme(serie);
    const c = [];
    const bon = pour === 'moyenne' ? S / n : pour === 'mediane' ? mediane(serie) : etendue(serie);
    const pousser = (code, v) => { if (v !== null && v !== undefined && Number.isFinite(v) && v > 0 && v !== bon && !c.some((x) => x.v === v)) c.push({ code, v }); };
    if (pour === 'moyenne') {
        pousser('mauvais_effectif', Number.isInteger(S / (n - 1)) ? S / (n - 1) : S / 2 % 1 === 0 ? S / 2 : null);
        pousser('somme_seule', S);
        pousser('mediane_pour_moyenne', mediane(serie));
        pousser('milieu_des_extremes', (r[0] + r[n - 1]) / 2);
        pousser('nombre_de_valeurs', n);
    } else if (pour === 'mediane') {
        pousser('milieu_sans_ranger', n % 2 ? serie[(n - 1) / 2] : (serie[n / 2 - 1] + serie[n / 2]) / 2);
        pousser('moyenne_pour_mediane', Number.isInteger(S / n) ? S / n : null);
        pousser('milieu_des_extremes', (r[0] + r[n - 1]) / 2);
        pousser('centrale_voisine', n % 2 ? r[(n - 1) / 2 + randomChoice([-1, 1])] : r[n / 2]);
        pousser('nombre_de_valeurs', n);
    } else {
        pousser('extremes_sans_ranger', Math.abs(serie[n - 1] - serie[0]));
        pousser('max_seul', r[n - 1]);
        pousser('somme_extremes', r[0] + r[n - 1]);
        pousser('nombre_de_valeurs', n);
        pousser('mediane_pour_moyenne', mediane(serie));
    }
    return c.filter((x) => Number.isInteger(x.v * 2));
};

const distracteurs = (pour, serie) => {
    const pris = [];
    for (const x of shuffle(candidats(pour, serie))) if (!pris.some((y) => y.code === x.code)) pris.push(x);
    return pris.length >= 3 ? pris.slice(0, 3) : null;
};

const valeurDe = (pour, serie) => (pour === 'moyenne' ? moyenneDemis(serie) / 2 : pour === 'mediane' ? mediane(serie) : etendue(serie));
const MOT = { moyenne: 'Moyenne', mediane: 'Médiane', etendue: 'Étendue' };
const ATTENDU = { moyenne: 1246, mediane: 1247, etendue: 1248 };
const LABEL_QCM = { moyenne: L_MOY, mediane: L_MED, etendue: L_ETD };

// ─── Les trois cartes, paramétrées par l'indicateur ───────────────────────

const genFlash = (pour) => () => {
    for (let essai = 0; essai < 100; essai++) {
        const series = [];
        for (let i = 0; i < 3; i++) series.push(tirer(pour, series.map(cle)));
        if (new Set(series.map(cle)).size < 3) continue;
        const phrase = series.map((s) => `${MOT[pour]} de ${texteSerie(s)} : {{${fr(valeurDe(pour, s))}}}`).join('\n');
        const payload = makeTexteTrou(phrase, '<p><b>Calcule.</b></p>', { ...opts(ATTENDU[pour], L_FLASH), mode: 'saisie', strict: false });
        payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle } };
        payload._meta.params = { pour, series };
        return payload;
    }
    const series = [tirer(pour), tirer(pour), tirer(pour)];
    const payload = makeTexteTrou(series.map((s) => `${MOT[pour]} de ${texteSerie(s)} : {{${fr(valeurDe(pour, s))}}}`).join('\n'), '<p><b>Calcule.</b></p>', { ...opts(ATTENDU[pour], L_FLASH), mode: 'saisie', strict: false });
    payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle } };
    payload._meta.params = { pour, series };
    return payload;
};

const genQcm = (pour) => () => {
    for (let essai = 0; essai < 200; essai++) {
        const serie = tirer(pour);
        const faux = distracteurs(pour, serie);
        if (!faux) continue;
        const bon = fr(valeurDe(pour, serie));
        const choix = [bon, ...faux.map((f) => fr(f.v))];
        if (new Set(choix).size !== 4) continue;
        const stem = `<p><b>La ${MOT[pour].toLowerCase()} de la série ${texteSerie(serie)} est…</b></p>`;
        const payload = makeQcm(choix, bon, stem, opts(ATTENDU[pour], LABEL_QCM[pour]));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { pour, serie, distracteurs: faux.map(({ code }) => ({ code })) };
        return payload;
    }
    const serie = pour === 'moyenne' ? [4, 11, 9] : pour === 'mediane' ? [7, 3, 9] : [9, 2, 14, 6];
    const bon = fr(valeurDe(pour, serie));
    const repli = { moyenne: ['24', '9', '12'], mediane: ['3', '6', '9'], etendue: ['14', '16', '3'] }[pour];
    const payload = makeQcm([bon, ...repli], bon, `<p><b>La ${MOT[pour].toLowerCase()} de la série ${texteSerie(serie)} est…</b></p>`, opts(ATTENDU[pour], LABEL_QCM[pour]));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { pour, serie, distracteurs: [] };
    return payload;
};

const genVf = (pour) => () => {
    for (let essai = 0; essai < 200; essai++) {
        const series = [];
        for (let i = 0; i < 4; i++) series.push(tirer(pour, series.map(cle)));
        if (new Set(series.map(cle)).size < 4) continue;
        const nbVraies = randomChoice([1, 2, 2, 3]);
        const verites = shuffle([...Array(nbVraies).fill(true), ...Array(4 - nbVraies).fill(false)]);
        const lignes = series.map((s, i) => {
            if (verites[i]) return { ligne: `La ${MOT[pour].toLowerCase()} de ${texteSerie(s)} est ${fr(valeurDe(pour, s))}.`, code: null };
            const cands = candidats(pour, s);
            if (!cands.length) return null;
            const x = randomChoice(cands);
            return { ligne: `La ${MOT[pour].toLowerCase()} de ${texteSerie(s)} est ${fr(x.v)}.`, code: x.code };
        });
        if (lignes.some((l) => !l)) continue;
        const payload = makeVraiFaux(lignes.map((l) => l.ligne), verites.map((v) => (v ? 0 : 1)), '<p><b>Vrai ou faux ?</b></p>', opts(ATTENDU[pour], L_VF));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { pour, series, verites, distracteurs: lignes.filter((l) => l.code).map(({ code }) => ({ code })) };
        return payload;
    }
    const series = [tirer(pour), tirer(pour), tirer(pour), tirer(pour)];
    const lignes = series.map((s, i) => `La ${MOT[pour].toLowerCase()} de ${texteSerie(s)} est ${fr(i % 2 ? valeurDe(pour, s) + 1 : valeurDe(pour, s))}.`);
    const payload = makeVraiFaux(lignes, [0, 1, 0, 1], '<p><b>Vrai ou faux ?</b></p>', opts(ATTENDU[pour], L_VF));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { pour, series, verites: [true, false, true, false], distracteurs: [] };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'séries brutes en désordre, entiers 1..30 ; quatre nombres distincts au QCM ; un distracteur de même valeur que la clé est retiré (moyenne = médiane, par exemple)',
        'sacrifiée : {3, 5, 8, 12, 17} dans tous les ordres (les trois indicateurs)',
    ],
    moy_qcm_unique: 'mauvais_effectif (÷ n − 1 ou ÷ 2, la principale), somme_seule, mediane_pour_moyenne, milieu_des_extremes, nombre_de_valeurs',
    med_qcm_unique: 'milieu_sans_ranger (la principale), moyenne_pour_mediane, milieu_des_extremes, centrale_voisine, nombre_de_valeurs',
    etd_qcm_unique: 'extremes_sans_ranger (la principale), max_seul, somme_extremes, nombre_de_valeurs, mediane_pour_moyenne',
    vf_tableau_vf: 'quatre séries distinctes, une à trois vraies, les fausses tirent une famille',
};

// ─── Registre et points d'entrée ──────────────────────────────────────────

const GENERATORS = {
    moy_flash_texte_trou_saisie: genFlash('moyenne'),
    moy_qcm_unique: genQcm('moyenne'),
    moy_tableau_vf: genVf('moyenne'),
    med_flash_texte_trou_saisie: genFlash('mediane'),
    med_qcm_unique: genQcm('mediane'),
    med_tableau_vf: genVf('mediane'),
    etd_flash_texte_trou_saisie: genFlash('etendue'),
    etd_qcm_unique: genQcm('etendue'),
    etd_tableau_vf: genVf('etendue'),
};

const categories = (prefixe, labelQcm) => [
    { id: `${prefixe}_flash`, label: L_FLASH, formatsList: [{ key: `${prefixe}_flash_texte_trou_saisie`, label: 'Saisie trous' }] },
    { id: `${prefixe}_qcm`, label: labelQcm, formatsList: [{ key: `${prefixe}_qcm_unique`, label: 'QCM' }] },
    { id: `${prefixe}_vf`, label: L_VF, formatsList: [{ key: `${prefixe}_tableau_vf`, label: 'Vrai/Faux' }] },
];
/** 1246 : la moyenne. */
export const CATEGORIES = categories('moy', L_MOY);
/** 1247 : la médiane (gen_1247). */
export const CATEGORIES_MEDIANE = categories('med', L_MED);
/** 1248 : l'étendue (gen_1248). */
export const CATEGORIES_ETENDUE = categories('etd', L_ETD);

/** Le point d'entrée partagé : `cles` restreint le tirage aux clés d'un attendu. */
export const genererParmi = (cles, typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = cles.filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(cles);
    } else key = randomChoice(cles);
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

const CLES_1246 = Object.keys(GENERATORS).filter((k) => k.startsWith('moy_'));
export const generate = (typeExercice) => genererParmi(CLES_1246, typeExercice);
export { ATTENDU_ID };
